import styled, { css, keyframes } from 'styled-components';

// ─────────────────────────────────────────────────────────────────
// Primitives UI partagées — remplacent les variantes ad hoc recréées
// dans chaque page. Toute nouvelle interface se construit avec elles.
// ─────────────────────────────────────────────────────────────────

/* ═══ Card ═══
   $pad permet de resserrer une carte qui contient déjà sa propre barre d'outils
   (tableaux). Pas d'overflow: hidden ici : les dropdowns positionnés en absolu
   à l'intérieur d'une carte seraient rognés. */
export const Card = styled.div`
    background: ${(p) => p.theme.surface};
    border: 1px solid ${(p) => p.theme.border};
    border-radius: ${(p) => p.theme.radius.md};
    box-shadow: ${(p) => p.theme.shadowSm};
    padding: ${(p) => p.$pad || '18px'};
    margin-bottom: 16px;
`;

/* ═══ Button — $variant: primary (défaut) | secondary | ghost | danger ═══ */
const buttonVariants = {
    primary: css`
        background: ${(p) => p.theme.accent};
        color: ${(p) => p.theme.onAccent};
        border-color: transparent;
        &:hover:not(:disabled) { background: ${(p) => p.theme.accentHover}; }
    `,
    secondary: css`
        background: ${(p) => p.theme.surface};
        color: ${(p) => p.theme.text};
        border-color: ${(p) => p.theme.borderStrong};
        &:hover:not(:disabled) { background: ${(p) => p.theme.surface3}; }
    `,
    ghost: css`
        background: none;
        color: ${(p) => p.theme.text2};
        border-color: transparent;
        &:hover:not(:disabled) { background: ${(p) => p.theme.surface3}; color: ${(p) => p.theme.text}; }
    `,
    danger: css`
        background: ${(p) => p.theme.danger};
        color: #fff;
        border-color: transparent;
        &:hover:not(:disabled) { opacity: 0.9; }
    `,
};

export const Button = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 7px;
    padding: 7px 14px;
    font-family: inherit;
    font-size: 0.85rem;
    font-weight: 700;
    border-radius: ${(p) => p.theme.radius.sm};
    border: 1px solid transparent;
    cursor: pointer;
    white-space: nowrap;
    transition: background 0.12s ease, border-color 0.12s ease;
    ${(p) => buttonVariants[p.$variant] || buttonVariants.primary}

    &:disabled {
        opacity: 0.45;
        cursor: not-allowed;
    }
`;

/* ═══ IconButton — action discrète (toolbar, topbar) ═══ */
export const IconButton = styled.button`
    width: 32px;
    height: 32px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: none;
    border: 1px solid ${(p) => (p.$bordered ? p.theme.border : 'transparent')};
    border-radius: ${(p) => p.theme.radius.sm};
    color: ${(p) => p.theme.text2};
    font-size: 1.1rem;
    cursor: pointer;
    transition: background 0.12s ease, color 0.12s ease;

    &:hover:not(:disabled) {
        background: ${(p) => p.theme.surface3};
        color: ${(p) => p.theme.text};
    }
    &:disabled {
        opacity: 0.45;
        cursor: not-allowed;
    }
`;

/* ═══ Input / Select ═══ */
const fieldStyles = css`
    padding: 7px 10px;
    font-size: 0.85rem;
    font-family: inherit;
    color: ${(p) => p.theme.text};
    background: ${(p) => p.theme.surface};
    border: 1px solid ${(p) => p.theme.borderStrong};
    border-radius: ${(p) => p.theme.radius.sm};
    transition: border-color 0.12s ease;

    &:focus {
        outline: none;
        border-color: ${(p) => p.theme.accent};
    }
    &:disabled {
        background: ${(p) => p.theme.surface2};
        cursor: not-allowed;
    }
`;

export const Input = styled.input`
    ${fieldStyles}
    &::placeholder { color: ${(p) => p.theme.text3}; }
`;

export const Select = styled.select`
    ${fieldStyles}
    cursor: pointer;
`;

/* ═══ Pill — $tone: neutral (défaut) | ok | warn | danger | accent | rssi ═══ */
const pillTones = {
    ok:      css`background: ${(p) => p.theme.okSoft};      color: ${(p) => p.theme.ok};`,
    warn:    css`background: ${(p) => p.theme.warnSoft};    color: ${(p) => p.theme.warn};`,
    danger:  css`background: ${(p) => p.theme.dangerSoft};  color: ${(p) => p.theme.danger};`,
    accent:  css`background: ${(p) => p.theme.accentSoft};  color: ${(p) => p.theme.accentText};`,
    rssi:    css`background: ${(p) => p.theme.rssiSoft};    color: ${(p) => p.theme.rssi};`,
    neutral: css`background: ${(p) => p.theme.surface3};    color: ${(p) => p.theme.text2};`,
};

export const Pill = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 2px 9px;
    font-size: 0.72rem;
    font-weight: 700;
    border-radius: ${(p) => p.theme.radius.pill};
    white-space: nowrap;
    ${(p) => pillTones[p.$tone] || pillTones.neutral}
`;

export const PillDot = styled.span`
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: currentColor;
    flex-shrink: 0;
`;

/* ═══ Chip — suggestion cliquable ═══ */
export const Chip = styled.button`
    background: ${(p) => p.theme.surface2};
    color: ${(p) => p.theme.text2};
    border: 1px solid ${(p) => p.theme.border};
    border-radius: ${(p) => p.theme.radius.pill};
    padding: 4px 12px;
    font-family: inherit;
    font-size: 0.78rem;
    cursor: pointer;
    transition: background 0.12s ease, color 0.12s ease;

    &:hover {
        background: ${(p) => p.theme.surface3};
        color: ${(p) => p.theme.text};
    }
`;

/* ═══ En-tête de page ═══ */
export const PageHead = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 12px;
    flex-wrap: wrap;
    margin-bottom: 18px;

    h2 {
        margin: 0;
        font-size: ${(p) => p.theme.type.pageTitle};
        font-weight: 800;
    }
    p {
        margin: 2px 0 0;
        color: ${(p) => p.theme.text2};
        font-size: ${(p) => p.theme.type.caption};
    }
`;

export const PageActions = styled.div`
    margin-left: auto;
    display: flex;
    gap: 8px;
    align-items: center;
`;

/* ═══ État vide ═══ */
export const EmptyState = styled.div`
    text-align: center;
    padding: 52px 20px;
    color: ${(p) => p.theme.text2};

    i {
        font-size: 2.1rem;
        color: ${(p) => p.theme.text3};
        display: block;
        margin-bottom: 10px;
    }
    h3 {
        font-size: ${(p) => p.theme.type.cardTitle};
        color: ${(p) => p.theme.text};
        margin: 0 0 4px;
    }
    p {
        margin: 0;
        font-size: ${(p) => p.theme.type.table};
    }
`;

/* ═══ Titre de carte ═══ */
export const CardTitle = styled.h2`
    margin: 0 0 14px;
    font-size: ${(p) => p.theme.type.cardTitle};
    font-weight: 800;
    color: ${(p) => p.theme.text};
`;

export const CardHeaderRow = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 14px;

    h2 {
        margin: 0;
        font-size: ${(p) => p.theme.type.cardTitle};
        font-weight: 800;
    }
`;

/* ═══ Formulaire ═══ */
export const FormRow = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin-bottom: 16px;
`;

export const FieldLabel = styled.label`
    font-size: ${(p) => p.theme.type.label};
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: ${(p) => p.theme.text2};

    small {
        display: block;
        margin-top: 2px;
        font-size: ${(p) => p.theme.type.caption};
        font-weight: 400;
        text-transform: none;
        letter-spacing: 0;
        color: ${(p) => p.theme.text3};
    }
`;

export const FormActions = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
`;

/* ═══ Message d'état — $tone: ok | warn | danger | accent ═══ */
const noticeTones = {
    ok:     css`background: ${(p) => p.theme.okSoft};     color: ${(p) => p.theme.ok};`,
    warn:   css`background: ${(p) => p.theme.warnSoft};   color: ${(p) => p.theme.warn};`,
    danger: css`background: ${(p) => p.theme.dangerSoft}; color: ${(p) => p.theme.danger};`,
    accent: css`background: ${(p) => p.theme.accentSoft}; color: ${(p) => p.theme.accentText};`,
};

export const Notice = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 8px;
    padding: 10px 13px;
    border-radius: ${(p) => p.theme.radius.sm};
    font-size: ${(p) => p.theme.type.body};
    line-height: 1.45;
    ${(p) => noticeTones[p.$tone] || noticeTones.accent}

    i {
        font-size: 1.05rem;
        flex-shrink: 0;
        margin-top: 1px;
    }
`;

/* ═══ Tableau de données ═══ */
export const TableScroll = styled.div`
    overflow-x: auto;
`;

export const DataTable = styled.table`
    width: 100%;
    border-collapse: collapse;
    font-size: ${(p) => p.theme.type.table};

    th {
        text-align: left;
        background: ${(p) => p.theme.surface2};
        color: ${(p) => p.theme.text2};
        font-size: ${(p) => p.theme.type.label};
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        padding: 9px 14px;
        border-bottom: 1px solid ${(p) => p.theme.border};
        white-space: nowrap;
    }

    td {
        padding: 9px 14px;
        border-bottom: 1px solid ${(p) => p.theme.border};
        color: ${(p) => p.theme.text};
        vertical-align: middle;
    }

    tbody tr:last-child td {
        border-bottom: none;
    }

    tbody tr {
        transition: background 0.1s ease;
    }

    tbody tr:hover {
        background: ${(p) => p.theme.surface3};
    }
`;

/* ═══ Spinner ═══ */
const spin = keyframes`
    to { transform: rotate(360deg); }
`;

export const Spinner = styled.div`
    width: ${(p) => p.$size || '36px'};
    height: ${(p) => p.$size || '36px'};
    border: 3px solid ${(p) => p.theme.surface3};
    border-top-color: ${(p) => p.theme.accent};
    border-radius: 50%;
    animation: ${spin} 0.7s linear infinite;
`;

export const SpinnerContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 48px 0;
`;
